import { type RefObject, useContext } from 'react';
import clsx from 'clsx';
import { Dietary } from '@hungryroot/constants';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { Loading } from '@hungryroot/loading-component-primitives';
import {
  useApiV3TagGroup as useTagGroup,
  tagGroups,
  tags,
} from '@hungryroot/tags';
import {
  Heading,
  Beef,
  BeansPlusLentils,
  Bison,
  Chicken,
  FreshFish,
  GroundBeef,
  Lamb,
  PlantBased,
  PlantBasedEgg,
  Pork,
  Salmon,
  Shellfish,
  SmokedSalmon,
  Tempeh,
  Trout,
  Tuna,
  Turkey,
  WhiteFish,
} from '@hungryroot/ui-kit';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { evictTagFromDislike } from '../../defs/avoidanceMutations';
import { getProteinSectionVisibility } from '../../defs/avoidances';
import {
  EatingHabit,
  ProteinTagTernaryRating,
  type ProteinTagTernaryValue,
  type TagId,
} from '../../defs/quiz';
import { useAvoidancesHabitPrune } from '../../hooks/useAvoidancesHabitPrune';
import { useFilteredQuizTags } from '../../hooks/useFilteredQuizTags';
import { QuizContext } from '../../quiz-context';
import { type QuizTernaryTagKeys } from '../../quiz-hooks';
import {
  proteinPreferenceAnalytics,
  type QuizPreferenceItem,
} from '../../segment/quizAnalytics';
import styles from './ProteinPreferences.module.css';
import layoutStyles from '../layout.module.css';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

// Meat illustration map — copied from Protein.tsx (Phase A hard constraint:
// variant 0's Protein.tsx must stay byte-identical, so this map is
// duplicated here rather than shared).
const meatIllustrationMap: Record<
  number,
  React.ComponentType<{ className?: string }>
> = {
  1: Beef,
  2: Chicken,
  3: Pork,
  16: Lamb,
  794: Bison,
  795: Beef,
  802: GroundBeef,
  803: Turkey,
  804: Chicken,
};

function getMeatIllustration({ tagId }: { tagId: number }) {
  return meatIllustrationMap[tagId] ?? Beef;
}

// Seafood illustration map — copied from Protein.tsx.
const seafoodIllustrationMap: Record<number, React.ComponentType> = {
  4: FreshFish,
  5: Shellfish,
  6: SmokedSalmon,
  796: Tuna,
  797: Trout,
  798: WhiteFish,
  801: Salmon,
};

function getSeafoodIllustration({ tagId }: { tagId: number }) {
  return seafoodIllustrationMap[tagId] ?? FreshFish;
}

// Plants illustration map — copied from Protein.tsx.
function getPlantsIllustration({ tagId }: { tagId: number }) {
  switch (tagId) {
    case 7:
      return Tempeh;
    case 9:
      return BeansPlusLentils;
    case 10:
      return PlantBased;
    case 13:
      return PlantBasedEgg;
    default:
      return PlantBased;
  }
}

function isPositivelyRated(
  tagId: number,
  ratings: ProteinTagTernaryValue[]
): boolean {
  return ratings.some(
    ({ id, rated }) =>
      id === tagId && rated === ProteinTagTernaryRating.Absolutely
  );
}

// Sub-copy branching — originally copied from Protein.tsx's `getSubCopy` (Phase
// A hard constraint: variant 0's Protein.tsx must stay byte-identical, so this
// is duplicated here rather than shared/exported from it). The two have since
// diverged: the omnivore/flexitarian branch below covers seafood as well, which
// is shortened-funnel copy only. Do not re-sync the branches from Protein.tsx.
function getSubCopy(habits: EatingHabit | null | undefined) {
  switch (habits) {
    case EatingHabit.Vegetarian:
    case EatingHabit.Vegan:
      return 'All our plant-based protein is non-GMO and contains zero animal-derived ingredients.';
    case EatingHabit.Pescatarian:
      return 'All our seafood is responsibly sourced, and our plant-based proteins are always non-GMO.';
    case EatingHabit.Omnivore:
    case EatingHabit.Flexitarian:
    default:
      return 'All of our meat & seafood is hormone-free + raised with no antibiotics ever.';
  }
}

export function ProteinPreferences(props: Props) {
  const { testid, scrollRef } = props;
  const { quiz, onQuestionAnswer, isMiniQuiz } = useContext(QuizContext);
  const { habits, dietary, meats, seafood, plants } = quiz;

  useScrollToTopFixIOS(scrollRef);

  const { getDisplayTagsFilter } = useFilteredQuizTags();

  const { meats: showMeat, seafood: showSeafood } =
    getProteinSectionVisibility(habits);
  const isVegetarianOrVegan =
    habits === EatingHabit.Vegetarian || habits === EatingHabit.Vegan;
  const isShellfishFree = dietary?.includes(Dietary.ShellfishFree);

  const { data: meatData } = useTagGroup({ tagGroupId: tagGroups.meatProtein });
  const { data: seafoodData } = useTagGroup({
    tagGroupId: tagGroups.seafoodProtein,
  });
  const { data: plantData } = useTagGroup({
    tagGroupId: tagGroups.plantProtein,
  });

  // Diet-change prune: a visitor who back-navigates to change EatingHabits
  // may have stale `meats`/`seafood`/`plants`/`dislike` entries that are no
  // longer eligible for the new diet. See `useAvoidancesHabitPrune` for why
  // this is gated on `quiz.avoidancesHabitsSnapshot` rather than firing on
  // every mount.
  useAvoidancesHabitPrune();

  // `onQuestionAnswer` is an absolute write keyed off the quiz snapshot it's
  // given, so a rating write followed by a dislike write must thread the
  // rating write's result into the dislike call — otherwise the rating is
  // silently dropped (see the matching handlers in Avoidances.tsx).
  function handleSelect({
    question,
    tagId,
    currentRatings,
  }: {
    question: QuizTernaryTagKeys;
    tagId: TagId;
    currentRatings: ProteinTagTernaryValue[];
  }) {
    if (isPositivelyRated(tagId, currentRatings)) {
      let nextQuiz = quiz;
      const nextRatings = currentRatings.filter(
        (rating) => rating.id !== tagId
      );
      onQuestionAnswer(nextQuiz, question, nextRatings);
      nextQuiz = { ...nextQuiz, [question]: nextRatings };

      onQuestionAnswer(nextQuiz, 'avoidancesHabitsSnapshot', habits);
      return;
    }

    const nextRatings: ProteinTagTernaryValue[] = [
      ...currentRatings.filter((rating) => rating.id !== tagId),
      { id: tagId, rated: ProteinTagTernaryRating.Absolutely },
    ];

    let nextQuiz = quiz;
    onQuestionAnswer(nextQuiz, question, nextRatings);
    nextQuiz = { ...nextQuiz, [question]: nextRatings };

    const nextDislike = evictTagFromDislike({
      tagId,
      dislike: nextQuiz.dislike ?? [],
    });
    onQuestionAnswer(nextQuiz, 'dislike', nextDislike);
    nextQuiz = { ...nextQuiz, dislike: nextDislike };

    onQuestionAnswer(nextQuiz, 'avoidancesHabitsSnapshot', habits);
  }

  function getPositiveItems(): QuizPreferenceItem[] {
    const sections: Array<{
      data: typeof meatData;
      ratings: ProteinTagTernaryValue[];
    }> = [
      { data: meatData, ratings: meats ?? [] },
      { data: seafoodData, ratings: seafood ?? [] },
      { data: plantData, ratings: plants ?? [] },
    ];

    return sections.flatMap(({ data, ratings }) => {
      if (data === undefined) return [];
      return data.tags[0]
        .filter((tag) => isPositivelyRated(tag.id, ratings))
        .map((tag) => ({ id: tag.id, name: tag.name, type: 'tag' as const }));
    });
  }

  if (
    (showMeat && meatData === undefined) ||
    (showSeafood && seafoodData === undefined) ||
    plantData === undefined
  ) {
    return (
      <QuizQuestionScreen
        heroText="What kinds of proteins do you like?"
        testid={testid}
        secondaryCyid="protein-preferences"
        sectionLabel={
          isMiniQuiz ? undefined : <SectionLabel label="Your tastes" />
        }
        subCopy={getSubCopy(habits)}
      >
        <Loading testid={`${testid}-loading`} variant="container" />
      </QuizQuestionScreen>
    );
  }

  return (
    <QuizQuestionScreen
      heroText="What kinds of proteins do you like?"
      testid={testid}
      secondaryCyid="protein-preferences"
      sectionLabel={
        isMiniQuiz ? undefined : <SectionLabel label="Your tastes" />
      }
      subCopy={getSubCopy(habits)}
      onSubmitAnalytics={() => {
        // Matches control's Protein.tsx shape exactly (see
        // ControlTagProteinRating): one 'Quiz Protein Preferences' event
        // per positively-rated tag, scalar `tag_id`/`tag_name`, so control
        // and this variant stay comparable under the same event name.
        getPositiveItems().forEach((item) => {
          proteinPreferenceAnalytics({
            id: item.id,
            name: item.name,
            preference: ProteinTagTernaryRating.Absolutely,
          });
        });
      }}
    >
      <div className={styles.content}>
        {showMeat && meatData && (
          <div className={styles.section}>
            {/*
             * Headings slide in on the same 0.5s beat as the first tile of
             * their grid: every grid's stagger restarts at 0.5s (the tile
             * `index` resets per section), so a static 0.5s delay keeps each
             * heading in step with the buttons under it.
             */}
            <Heading
              variant="H6"
              className={clsx(
                styles['section-heading'],
                layoutStyles['slide-in'],
                layoutStyles['animation-delay-point-5s']
              )}
            >
              Meat
            </Heading>
            <div className={styles.grid} data-testid={`${testid}-meat-grid`}>
              {meatData.tags[0]
                .filter(getDisplayTagsFilter('meat'))
                .map((tag, index) => {
                  const { id: tagId, name: tagName } = tag;
                  return (
                    <ProteinPreferenceTile
                      key={tagId}
                      testid={`${testid}-toggle-tag-${tagId}`}
                      tagName={tagName}
                      Icon={getMeatIllustration({ tagId })}
                      selected={isPositivelyRated(tagId, meats ?? [])}
                      index={index}
                      onSelect={() =>
                        handleSelect({
                          question: 'meats',
                          tagId,
                          currentRatings: meats ?? [],
                        })
                      }
                    />
                  );
                })}
            </div>
          </div>
        )}
        {showSeafood && seafoodData && (
          <div className={styles.section}>
            <Heading
              variant="H6"
              className={clsx(
                styles['section-heading'],
                layoutStyles['slide-in'],
                layoutStyles['animation-delay-point-5s']
              )}
            >
              Seafood
            </Heading>
            <div className={styles.grid} data-testid={`${testid}-seafood-grid`}>
              {seafoodData.tags[0]
                .filter(getDisplayTagsFilter('seafood'))
                .filter(
                  (tag) => !(isShellfishFree && tag.id === tags.Shellfish)
                )
                .map((tag, index) => {
                  const { id: tagId, name: tagName } = tag;
                  return (
                    <ProteinPreferenceTile
                      key={tagId}
                      testid={`${testid}-toggle-tag-${tagId}`}
                      tagName={tagName}
                      Icon={getSeafoodIllustration({ tagId })}
                      selected={isPositivelyRated(tagId, seafood ?? [])}
                      index={index}
                      onSelect={() =>
                        handleSelect({
                          question: 'seafood',
                          tagId,
                          currentRatings: seafood ?? [],
                        })
                      }
                    />
                  );
                })}
            </div>
          </div>
        )}
        <div className={styles.section}>
          {!isVegetarianOrVegan && (
            <Heading
              variant="H6"
              className={clsx(
                styles['section-heading'],
                layoutStyles['slide-in'],
                layoutStyles['animation-delay-point-5s']
              )}
            >
              Plant-based
            </Heading>
          )}
          <div className={styles.grid} data-testid={`${testid}-plants-grid`}>
            {plantData.tags[0]
              .filter(getDisplayTagsFilter('plantBased'))
              .filter((tag) => tag.id !== tags.DairyFreeCheese)
              .map((tag, index) => {
                const { id: tagId, name: tagName } = tag;
                return (
                  <ProteinPreferenceTile
                    key={tagId}
                    testid={`${testid}-toggle-tag-${tagId}`}
                    tagName={tagName}
                    Icon={getPlantsIllustration({ tagId })}
                    selected={isPositivelyRated(tagId, plants ?? [])}
                    index={index}
                    onSelect={() =>
                      handleSelect({
                        question: 'plants',
                        tagId,
                        currentRatings: plants ?? [],
                      })
                    }
                  />
                );
              })}
          </div>
        </div>
      </div>
    </QuizQuestionScreen>
  );
}

interface ProteinPreferenceTileProps {
  testid: string;
  tagName: string;
  Icon: React.ComponentType<{ className?: string }>;
  selected: boolean;
  index: number;
  onSelect: () => void;
}

function ProteinPreferenceTile({
  testid,
  tagName,
  Icon,
  selected,
  index,
  onSelect,
}: ProteinPreferenceTileProps) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={selected}
      className={clsx(styles.tile, layoutStyles['slide-in'], {
        [styles['tile-selected']]: selected,
      })}
      style={
        {
          '--slide-in-delay': `${0.05 * index + 0.5}s`,
        } as React.CSSProperties
      }
      onClick={onSelect}
    >
      <Icon className={styles.icon} />
      <div className={styles.text}>
        <Heading variant="H6">{tagName}</Heading>
      </div>
    </button>
  );
}
